import React from 'react'
import classNames from 'classnames'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { AsH3, CaptionSM } from '@/atoms/Text'
import { useFormatDuration } from '@/utils/EpisodeUtil'
import { useTranslate } from '@/utils/translate/translate-client'

type EpisodeDescriptionProps = {
  title?: string
  subtitle?: string
  displayDescription?: boolean
  description?: string
  href: string
  className?: string
  episodeNumber?: number
  duration?: number
  projectType?: 'series' | 'movie' | 'special' | 'podcast' | undefined
  prerelease?: boolean
  watchableType?: string
  /** When false (wrapped in parent link), renders caret as a div instead of a link to avoid nested links. */
  isWrappedInLink?: boolean
}

export const EpisodeDescription: React.FC<EpisodeDescriptionProps> = ({
  title,
  subtitle,
  displayDescription,
  description,
  href,
  className,
  episodeNumber,
  duration,
  projectType,
  prerelease,
  watchableType,
  isWrappedInLink = false,
}) => {
  const { t } = useTranslate('watch')
  const formatDuration = useFormatDuration(t)
  const isSeries = projectType === 'series'
  const episodeLabel = episodeNumber
    ? `${t('episode', 'Episode')} ${episodeNumber}`
    : watchableType === 'BONUS_CONTENT' || watchableType === 'TRAILER'
    ? t('bonus', 'Bonus')
    : null
  const durationLabel = typeof duration === 'number' ? formatDuration(duration) : undefined
  const seriesDisplayTitle = title?.trim() ? title : subtitle
  const nonSeriesLabel = watchableType === 'MOVIE' ? t('movie', 'Movie') : title
  const shouldHideTitle = Boolean(prerelease)

  return (
    <div
      className={classNames(
        'space-between max-w-[742px] flex w-full flex-row items-center h-full gap-x-4 lg:gap-x-0',
        className,
      )}
    >
      <div className="flex w-full flex-col gap-[6px]">
        {episodeLabel && isSeries && (
          <CaptionSM
            color="core-gray-500"
            className="md:photon-caption-md lg:photon-caption-lg transition-colors group-hover:text-white"
          >
            {episodeLabel}
          </CaptionSM>
        )}
        {!isSeries && nonSeriesLabel && (
          <CaptionSM
            color="core-gray-500"
            className="md:photon-caption-md lg:photon-caption-lg transition-colors group-hover:text-white"
          >
            {nonSeriesLabel}
          </CaptionSM>
        )}
        {isSeries && seriesDisplayTitle && !shouldHideTitle && (
          <CaptionSM
            as={AsH3}
            weight="bold"
            color="white"
            aria-label={seriesDisplayTitle}
            className="md:photon-title-md"
          >
            {seriesDisplayTitle}
          </CaptionSM>
        )}
        {!isSeries && subtitle && !shouldHideTitle && (
          <CaptionSM as={AsH3} weight="bold" color="white" aria-label={subtitle} className="md:photon-title-md">
            {subtitle}
          </CaptionSM>
        )}
        {durationLabel && (
          <CaptionSM color="core-gray-500" className="transition-colors group-hover:text-white">
            {durationLabel}
          </CaptionSM>
        )}
        {displayDescription && description && (
          <div className="hidden md:flex">
            <CaptionSM
              color="core-gray-500"
              aria-label={href.includes('/community/stories') ? 'video-story-description' : 'episode-description'}
              className="lg:photon-paragraph-md transition-300 line-clamp-4 text-core-gray-500 transition-colors ease-in-out group-hover:!text-white 2xl:line-clamp-6"
            >
              {description}
            </CaptionSM>
          </div>
        )}
      </div>
      {isWrappedInLink ? (
        <div className="lg:hidden">
          <CaretRightIcon size={24} color="white" />
        </div>
      ) : (
        <a className="lg:hidden" href={href}>
          <CaretRightIcon size={24} color="white" />
        </a>
      )}
    </div>
  )
}
